import { isForbidden } from '@agos/api-client'
import { Link } from 'wouter'
import {
  useGetEmployeesIdDriverWallet,
  useGetEmployeesIdWaiterWallet,
  useGetEmployeesWallets,
} from '@agos/api-client/hr'
import {
  useGetOrderTabs,
  useGetOrdersDailySummary,
  useGetSalesChannels,
} from '@agos/api-client/orders'
import { T, cardPayOnlineLabel, qrPayFigure, qrPayLabel, formatCents, formatDateTime, formatDay, parseMoneyToCents, toDateParam, tt, channelLabel, filterChannelsOf, type FilterChannel, noWalletNote, OrderNumberLink, ownWalletKind, useAuth, useModalFocus, walletsScope, alsoDrivesOf } from '@agos/shared'
import {
  SALES_MONEY_COLUMNS,
  categoryState,
  salesBreakdownOf,
  salesColumnLabel,
  salesGapCents,
  salesItemKey,
  salesRowLabel,
  salesTotals,
  selectedTotals,
  toggleCategory,
  toggleItem,
  type SalesCategoryRow,
} from './sales-breakdown'
import {
  BUTTON_PRIMARY,
  cn,
  type Column,
  DataTable,
  EmptyState,
  FilterBar,
  PageHeader,
  ReadFailedBlock,
  Spinner,
  StatCard,
  StatusBadge,
  TabGroup,
} from '@agos/ui'
import {
  AlertTriangle,
  BarChart3,
  Banknote,
  ChevronRight,
  CreditCard,
  Globe,
  MapPin,
  QrCode,
  Receipt,
  Search,
  Tag,
  Truck,
  UserCheck,
  Wallet,
  X,
} from 'lucide-react'
import { Fragment, useMemo, useRef, useState, type ReactNode } from 'react'

import { tabsOf, totalsOf } from '../order-tabs/order-tabs'

import {
  byRole,
  byWallet,
  channelLines,
  initialsOf,
  orderWord,
  reconcile,
  type DaySummary,
  type WalletEmployee,
} from './wallet-reconciliation'
import { unattributedArchiveLink } from './unattributed-link'
import {
  SUMMARIES_REVENUE,
  SUMMARIES_WALLETS,
  colleaguesWalletsNote,
  totalWithheldNote,
  walletsScopeWithTheRight,
  walletsScopeWithTheTotal,
} from './total-access'

/**
 * `/summaries` — a day's takings, and who is carrying them.
 *
 * Legacy's layout: the hero total with the average order, the payment-method cards,
 * and the waiter and driver wallet tables that open a per-employee detail modal.
 *
 * **FB-075** is what it does not do. The hero and the wallets are two answers to the
 * same question from the same rows, and on 28 July in the restored data they differ
 * by 42,85 € — orders with no waiter and no driver on them. A manager reading a
 * wallet table under a headline total assumes the rows account for it. Now the page
 * says how much they do not, and in which payment methods.
 *
 * Also added: the channel split the backend has sent since §10A.1 and this page has
 * never read.
 *
 * Ported from legacy `pages/summaries.tsx`.
 */

const TYPE_COLORS: Record<string, 'blue' | 'green' | 'amber'> = {
  delivery: 'blue',
  restaurant: 'green',
  bar: 'amber',
}

const TYPE_LABELS: Record<string, string> = {
  delivery: tt('Rozvoz'),
  restaurant: tt('Reštaurácia'),
  bar: tt('Osobný odber'),
}

const PAYMENT_LABELS: Record<string, string> = {
  cash: tt('Hotovosť'),
  card_terminal: tt('Karta – terminál'),
  card_courier: tt('Karta – kuriér'),
  // §9.767 - the online card's one name; it said *CardPay Online* here.
  card_online: cardPayOnlineLabel(),
  card: tt('Karta'),
  // §9.741, BACKEND.md §10A.390 - a door QR payment, which the wallet's method column printed raw.
  qr_pay: qrPayLabel(),
}

/**
 * §9.94 — the franchise's own name for the channel, with the hand-written map as the fallback for a
 * slug the API has not returned yet. This read the map alone, so a renamed channel kept its old
 * label and `na_sekeru` printed raw.
 */
function typeLabel(type: string | undefined, channels: readonly FilterChannel[]): string {
  if (!type) return ''
  return channelLabel(type, channels, TYPE_LABELS)
}

function paymentLabel(method: string | undefined): string {
  if (!method) return ''
  return PAYMENT_LABELS[method] ?? method
}

function today(): string {
  return toDateParam(new Date())
}

function yesterday(): string {
  const date = new Date()
  date.setDate(date.getDate() - 1)
  return toDateParam(date)
}

export function SummariesPage() {
  const [picked, setPicked] = useState(today)
  const [loaded, setLoaded] = useState(today)
  /**
   * §9.665, owner O49 - *"add also filter for more days, like.. from 5 days ago to today"*.
   *
   * **A second date rather than a replaced one.** `loaded` stays the start of the window and this is its end, so
   * every shortcut, every label and the two wallet details below keep working on a single day - which is what the
   * screen is for most of the time. An empty end means *that one day*, which is what `GET /orders/daily-summary`
   * does with an absent `dateTo` (§10A.335).
   */
  const [pickedTo, setPickedTo] = useState('')
  const [loadedTo, setLoadedTo] = useState('')
  /* The end of the window, or the start when there is no end - so one expression answers *what period is drawn*
     and the reads, the labels and the archive link cannot disagree about it. */
  const windowEnd = loadedTo === '' ? loaded : loadedTo
  const isRange = windowEnd !== loaded
  /**
   * What the summary read asks for - §9.665, §10A.335.
   *
   * **`dateFrom`/`dateTo` are the names `GET /employees/wallets` on this same screen already takes**, so the two
   * reads cannot be asked for different windows by accident; `?from=…&to=…` would be a third spelling of a range in
   * this product, and §9.613 measured what that costs - an unknown parameter name is ignored rather than refused, so
   * the screen reads the server's own default window while believing it asked for something else.
   *
   * **A named variable rather than an inline literal**, which is §9.509's recorded shape: TypeScript's
   * excess-property check fires only on a fresh object literal, so this reaches the generated hook with no cast and
   * nothing under `src/generated` touched until the client is regenerated from a running backend.
   */
  const summaryWindow = { date: loaded, dateFrom: loaded, dateTo: windowEnd }
  const [openDriverId, setOpenDriverId] = useState<number | null>(null)
  const [openWaiterId, setOpenWaiterId] = useState<number | null>(null)

  /* §9.729, owner O99 - the day's total is the right `summaries_revenue` (BACKEND.md §10A.377), apart from the screen's
     own `summaries`. Asked before the read rather than after a refusal: a 403 that is never provoked needs no
     sentence (§9.661), and the one that stands in for the total says why it is not there. */
  const { user, hasPermission } = useAuth()
  const maySeeTotal = hasPermission(SUMMARIES_REVENUE)

  const {
    data: summaryData,
    isFetching: summaryFetching,
    isError: summaryFailed,
    refetch: refetchSummary,
  } = useGetOrdersDailySummary(summaryWindow, { query: { enabled: maySeeTotal } })
  /* FB-818, §9.539 - **the wallets read is the one that can invent a discrepancy.** `walletData ?? []` makes
     `walletTotals` nought, `unattributedCents` becomes the whole day's revenue, and `TOLERANCE_CENTS` is **0**, so
     any non-zero difference trips the amber line - which then names a cause (*orders with no waiter and no
     driver*) and links to a list that will be empty. Every other instance of this class fabricates an all-clear;
     this one fabricates an accusation. */
  /**
   * §9.661, FB-989 - **asked before it is read.** `/summaries` is guarded on the `summaries` **permission** and
   * `GET /employees/wallets` on the `main_admin`/`admin` **role**, so a waiter granted this screen got a 403 that
   * `isError` reported as *the wallets could not be loaded, so we do not know whether the whole day is attributed*
   * - a fabricated doubt about the day's money, which is what FB-818's banner exists to prevent.
   */
  const ownKind = ownWalletKind(user?.systemRole, alsoDrivesOf(user))
  /* §9.803, owner O150 - *"some permission for showing sumáre from everyone"*: whoever holds `summaries_wallets` is
     shown everyone's, as an administrator is (BACKEND.md §10A.449 admits them to the same reads). */
  const mayReadEveryone = hasPermission(SUMMARIES_WALLETS)
  /* §9.729 - and the overview of every wallet adds up to the attributed takings, so somebody without the total is not
     shown it either: their own wallet where their role keeps one, nothing otherwise. Narrows, never widens. */
  const scope = walletsScopeWithTheTotal(
    walletsScopeWithTheRight(walletsScope(user?.systemRole, alsoDrivesOf(user)), mayReadEveryone),
    maySeeTotal,
    ownKind,
  )

  const {
    data: walletData,
    isLoading: walletsLoading,
    isError: walletsFailed,
    error: walletsError,
    refetch: refetchWallets,
  } = useGetEmployeesWallets(
    {
      dateFrom: loaded,
      dateTo: windowEnd,
    },
    // A 403 that is never provoked needs no sentence, which is better than a better sentence.
    { query: { enabled: scope === 'everyone' } },
  )

  /* The case that remains: an administrator whose right was revoked mid-session, or a server older than §9.661.
     A **refusal** is not evidence about the money, so it gets its own muted line and the reconciliation warning
     is withheld rather than reworded - that warning is an accusation (FB-818). */
  const walletsRefused = scope === 'everyone' && isForbidden(walletsError)
  const walletsBroken = walletsFailed && !walletsRefused

  const summary = summaryData as DaySummary | undefined
  const wallets = useMemo<WalletEmployee[]>(() => walletData ?? [], [walletData])

  // Read, not computed: `/order-tabs` is the open ones, already filtered by the server — the same
  // source `/revenue-handovers` uses, so the two pages cannot drift apart about it.
  const openTabs = useGetOrderTabs()
  const openTabsCents = totalsOf(tabsOf(openTabs.data)).outstandingCents

  const waiters = useMemo(() => byWallet(byRole(wallets, 'waiter')), [wallets])
  const drivers = useMemo(() => byWallet(byRole(wallets, 'driver')), [wallets])
  const check = reconcile(summary, wallets)
  const channels = channelLines(summary)

  const orderCount = summary?.orderCount ?? 0

  /* §9.729 - drawn under the day's figures, and on its own where the total is withheld: one panel, so the own
     peňaženka (O47) and the overview cannot come to differ between the two. */
  const walletsPanel = (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <h3 className="text-lg font-black flex items-center gap-2">
          <Wallet className="w-5 h-5 text-primary" />
          <T>Peňaženky zamestnancov</T>
        </h3>
      </div>

      {/* FB-075 - the hero and these rows answer the same question from the
          same orders, and they do not always agree. */}
      {/* FB-818, §9.539 - **withheld, not suppressed and not corrected downward.** Suppressing it would be
          the all-clear this whole class is about; showing a zero would claim a reconciliation nobody
          checked. `reconcile` is unchanged - it is arithmetic over two inputs and right about both, and the
          caller is what knows whether an input arrived (§9.538's seam). */}
      {/* §9.661 - **in `self` scope the overview is never asked for**, so none of the three blocks below
          applies: the person is shown their own wallet instead and told so, rather than a doubt about a
          reconciliation they were not shown the inputs for. */}
      {scope !== 'everyone' && (
        <div
          className="px-6 py-3 border-b border-border text-xs text-muted-foreground flex items-start gap-2"
          data-own-wallet-scope
        >
          <Wallet className="w-4 h-4 shrink-0 mt-px" />
          {ownKind === null ? (
            <span>{noWalletNote()}</span>
          ) : (
            <span>
              {/* §9.803 - names the right that is missing, as the withheld total does (§9.728). */}
              {colleaguesWalletsNote(mayReadEveryone)}{' '}
              <button
                type="button"
                data-own-wallet-open
                onClick={() =>
                  ownKind === 'waiter'
                    ? setOpenWaiterId(user?.id ?? null)
                    : setOpenDriverId(user?.id ?? null)
                }
                className="underline font-semibold hover:no-underline"
              >
                <T>Moja peňaženka</T>
              </button>
            </span>
          )}
        </div>
      )}

      {/* §9.661 - a **refusal** rather than a fault, and it says so without the accusation attached. The
          server's own guard is a role, so this is an administrator whose right was revoked mid-session or a
          server older than that entry. */}
      {walletsRefused && (
        <div
          className="px-6 py-3 border-b border-border text-xs text-muted-foreground flex items-start gap-2"
          data-wallets-refused
        >
          <Wallet className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <T>Peňaženky kolegov vám tento účet nesmie zobraziť, takže tu nie sú.</T>
          </span>
        </div>
      )}

      {walletsBroken ? (
        <div
          className="px-6 py-3 bg-amber-500/10 border-b border-amber-500/30 text-xs text-amber-500 flex items-start gap-2"
          data-unattributed-unknown
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <T>
              Peňaženky zamestnancov sa nepodarilo načítať, takže nevieme, či je celá tržba dňa priradená.
            </T>{' '}
            <button
              type="button"
              onClick={() => void refetchWallets()}
              data-unattributed-retry
              className="underline font-semibold hover:no-underline"
            >
              <T>Skúsiť znova</T>
            </button>
          </span>
        </div>
      ) : null}

      {scope === 'everyone' && !walletsFailed && !walletsLoading && !check.reconciles && (
        <div
          className="px-6 py-3 bg-amber-500/10 border-b border-amber-500/30 text-xs text-amber-500 flex items-start gap-2"
          data-unattributed
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <T>Peňaženky nepokrývajú celú tržbu dňa. Bez zamestnanca je</T>{' '}
            <strong>{formatCents(check.unattributedCents)}</strong>
            {check.gaps.length > 0 && (
              <>
                {' - '}
                {check.gaps.map((gap) => `${gap.label} ${formatCents(gap.cents)}`).join(', ')}
              </>
            )}
            . <T>Sú to objednávky, na ktorých nie je čašník ani šofér.</T>{' '}
            {/* §9.451, FB-769 - the owner clicked this warning and nothing happened, because
             * it was not a link and could not be: the figure is a subtraction of two sums.
             * §10A.214 gave `/orders` the filter it needed. */}
            <Link
              href={unattributedArchiveLink(loaded)}
              data-unattributed-link
              className="underline font-semibold hover:no-underline"
            >
              <T>Zobraziť tieto objednávky</T>
            </Link>
          </span>
        </div>
      )}

      {/* *Za toto obdobie žiadni zamestnanci nemajú objednávky* is **evidential** - FB-812's distinction -
          and it was drawn from the same failed read that had just accused them. */}
      {scope !== 'everyone' || walletsFailed ? null : walletsLoading ? (
        <div className="py-8 flex items-center justify-center">
          <Spinner className="w-5 h-5" />
        </div>
      ) : waiters.length === 0 && drivers.length === 0 ? (
        <div className="py-10 text-center text-muted-foreground text-sm">
          <T>Za toto obdobie žiadni zamestnanci nemajú objednávky.</T>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {waiters.length > 0 && (
            <WalletSection
              icon={<UserCheck className="w-3.5 h-3.5" />}
              title={tt('Čašníci')}
              wallets={waiters}
              onOpen={setOpenWaiterId}
              testId="waiters"
            />
          )}
          {drivers.length > 0 && (
            <WalletSection
              icon={<Truck className="w-3.5 h-3.5" />}
              title={tt('Šoféri')}
              wallets={drivers}
              onOpen={setOpenDriverId}
              testId="drivers"
            />
          )}
        </div>
      )}
    </div>
  )

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Prehľady')}
        description={tt('Denné tržby a peňaženky zamestnancov')}
        icon={<BarChart3 className="w-5 h-5" />}
      />

      {/* §9.5, the other half of what `/revenue-handovers` already says. `HandoverReconciliation`
       * filters `AND tab_id IS NULL`, so an open tab is not part of what anybody hands over — and
       * `WalletService`, which builds these wallets, does **not** filter it. So a wallet and a
       * handover differ by exactly the open tabs, and this page is where both are read. */}
      {openTabsCents > 0 && (
        <p className="text-[11px] text-muted-foreground" data-open-tabs>
          <T>Na otvorených účtoch je</T> <strong>{formatCents(openTabsCents)}</strong>{' '}
          <T>
            - je to v peňaženkách nižšie, ale nie v tom, čo sa odovzdáva. Sedieť to začne až po
            vyrovnaní účtu.
          </T>

        </p>
      )}

      <FilterBar
        filters={
          <>
            <input
              type="date"
              value={picked}
              onChange={(event) => setPicked(event.target.value)}
              aria-label={tt('Dátum')}
              className="px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all"
            />
            {/* §9.665, owner O49 - *"add also filter for more days, like.. from 5 days ago to today"*. Empty means
              * *that one day*, so the screen opens exactly as it did and a franchise that never wants a range never
              * sees one. Labelled *Do dátumu (nepovinné)* rather than left bare, because an empty second date box
              * beside a filled one reads as a field somebody forgot. */}
            <span className="text-sm text-muted-foreground">-</span>
            <input
              type="date"
              value={pickedTo}
              min={picked}
              onChange={(event) => setPickedTo(event.target.value)}
              aria-label={tt('Do dátumu (nepovinné)')}
              data-summary-to
              className="px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all"
            />
            <button
              type="button"
              onClick={() => {
                setLoaded(picked)
                setLoadedTo(pickedTo)
              }}
              disabled={summaryFetching || !picked}
              className={cn(
                'flex items-center gap-2 px-5 py-2 rounded-xl',
                BUTTON_PRIMARY,
                'text-sm',
              )}
            >
              {summaryFetching ? <Spinner className="w-4 h-4" /> : <Search className="w-4 h-4" />}
              <T>Načítať</T>
            </button>
            <span className="text-sm text-muted-foreground">
              <T>Zobrazené:</T>{' '}
              <strong className="text-foreground" data-loaded-date>
                {/* §9.665 - the label says the window rather than the first day of it, because every figure below
                  * covers the whole of it and a single date over a five-day total is the wrong claim. */}
                {windowLabel(loaded, windowEnd)}
              </strong>
            </span>
          </>
        }
        actions={
          <div className="flex gap-2 flex-wrap">
            {[
              { label: tt('Dnes'), date: today() },
              { label: tt('Včera'), date: yesterday() },
            ].map((shortcut) => (
              <button
                key={shortcut.label}
                type="button"
                onClick={() => {
                  setPicked(shortcut.date)
                  setLoaded(shortcut.date)
                  // §9.665 - a shortcut is one day, so it clears the end rather than leaving a range the label
                  // would then contradict.
                  setPickedTo('')
                  setLoadedTo('')
                }}
                className={cn(
                  'px-3 py-1.5 rounded-xl text-xs font-bold border-2 transition-all',
                  loaded === shortcut.date && !isRange
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:border-muted-foreground',
                )}
              >
                {shortcut.label}
              </button>
            ))}
          </div>
        }
      />

      {/* FB-818, §9.539 - the milder half. `!summary` catches a failed read before the 5xl `Celková tržba`
          renders, so the money **cannot** fabricate a zero - which is luck, since that guard exists for the
          screen's initial state. What it said was *Vyberte dátum a kliknite Načítať*, **an instruction the
          operator has already followed**, so the failure gets its own sentence and the instruction keeps the
          state it was written for. */}
      {summaryFailed ? (
        <ReadFailedBlock
          marker="summary"
          title={tt('Súhrn dňa sa nepodarilo načítať')}
          onRetry={() => void refetchSummary()}
        >
          <T>Tržbu za tento deň preto nezobrazujeme - nulové číslo by nebolo skutočné.</T>
        </ReadFailedBlock>
      ) : !maySeeTotal ? (
        /* §9.729, owner O99 - without `summaries_revenue` the day summary was never asked for, so none of its
           figures is drawn; one sentence stands where they were, and the own peňaženka is still offered. */
        <>
          <div
            className="bg-card border border-border rounded-2xl px-6 py-5 text-sm text-muted-foreground flex items-start gap-2"
            data-summary-total-withheld
          >
            <BarChart3 className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{totalWithheldNote()}</span>
          </div>
          {walletsPanel}
        </>
      ) : summaryFetching && !summary ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : !summary ? (
        <div className="py-16">
          <EmptyState
            icon={<BarChart3 className="w-10 h-10" />}
            description={tt('Vyberte dátum a kliknite Načítať.')}
          />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-4" data-day-summary>
            <div className="bg-card border border-border rounded-2xl p-6 flex flex-col justify-between min-w-[220px]">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">
                  <T>Celková tržba</T>
                </p>
                <p className="text-5xl font-black leading-none tracking-tight tabular-nums">
                  {formatCents(check.dayCents)}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  {orderCount} {orderWord(orderCount)}
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">
                  <T>Priemerná objednávka</T>
                </p>
                <p className="text-xl font-semibold tabular-nums">
                  {orderCount > 0 ? formatCents(parseMoneyToCents(summary.avgOrder)) : '---'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <StatCard
                label={tt('Hotovosť')}
                value={formatCents(parseMoneyToCents(summary.cashTotal))}
                icon={Banknote}
                color="emerald"
              />
              <StatCard
                label={tt('Karta')}
                value={formatCents(parseMoneyToCents(summary.cardTerminalTotal))}
                icon={CreditCard}
                color="blue"
              />
              <StatCard
                label={tt('Kuriér karta')}
                value={formatCents(parseMoneyToCents(summary.cardCourierTotal))}
                icon={CreditCard}
                color="orange"
              />
              {/* §9.767, owner O129 - the online card's one name (it said *Online*), and QR Pay always beside
                  it: a 0,00 € is a day with no door QR payment, as the other tiles' noughts always were. */}
              <StatCard
                label={cardPayOnlineLabel()}
                value={formatCents(parseMoneyToCents(summary.cardOnlineTotal))}
                icon={Globe}
                color="violet"
                testId="day-cardpay-online"
              />
              <StatCard
                label={qrPayLabel()}
                value={qrPayFigure(parseMoneyToCents(summary.qrPayTotal))}
                icon={QrCode}
                color="pink"
                testId="day-qr-pay"
              />
              <StatCard
                label="Doprava"
                value={formatCents(parseMoneyToCents(summary.totalDelivery))}
                icon={Truck}
                color="primary"
              />
              <StatCard
                label={tt('Zľavy')}
                value={formatCents(parseMoneyToCents(summary.totalDiscount))}
                icon={Tag}
                color={parseMoneyToCents(summary.totalDiscount) > 0 ? 'amber' : 'primary'}
              />
              <StatCard
                label="Storno"
                value={`${summary.cancelledCount ?? 0} obj.`}
                sub={
                  (summary.cancelledCount ?? 0) > 0
                    ? formatCents(parseMoneyToCents(summary.cancelledTotal))
                    : undefined
                }
                icon={Receipt}
                color={(summary.cancelledCount ?? 0) > 0 ? 'red' : 'primary'}
              />
            </div>
          </div>

          {/* Not in legacy: the backend has sent this since §10A.1. */}
          {channels.length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-5" data-channel-split>
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">
                <T>Podľa predajného kanála</T>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {channels.map((channel) => (
                  <div key={channel.name} className="bg-muted/40 rounded-xl p-3">
                    <p className="text-sm font-semibold truncate">{channel.name}</p>
                    <p className="text-lg font-bold tabular-nums">{formatCents(channel.cents)}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {channel.orderCount} {orderWord(channel.orderCount)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {walletsPanel}
        </>
      )}

      {openDriverId !== null && (
        // §9.696, FB-1004 - the window the row was clicked in, both ends, not its first day.
        <DriverDetail
          driverId={openDriverId}
          dateFrom={loaded}
          dateTo={windowEnd}
          onClose={() => setOpenDriverId(null)}
        />
      )}
      {openWaiterId !== null && (
        <WaiterDetail
          waiterId={openWaiterId}
          dateFrom={loaded}
          dateTo={windowEnd}
          onClose={() => setOpenWaiterId(null)}
        />
      )}
    </div>
  )
}

function WalletSection({
  icon,
  title,
  wallets,
  onOpen,
  testId,
}: {
  icon: ReactNode
  title: string
  wallets: WalletEmployee[]
  onOpen: (id: number) => void
  testId: string
}) {
  const columns: Column<WalletEmployee>[] = useMemo(
    () => [
      {
        key: 'name',
        header: tt('Meno'),
        sortable: true,
        sortValue: (employee) => employee.name,
        render: (employee) => (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center text-primary text-xs font-bold shrink-0">
              {initialsOf(employee.name)}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate flex items-center gap-1.5">
                {employee.name}
                {employee.isExternal && <StatusBadge label={tt('Externý')} color="orange" />}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {employee.orderCount ?? 0} {orderWord(employee.orderCount ?? 0)}
              </p>
            </div>
          </div>
        ),
      },
      moneyColumn('walletTotal', tt('Celková tržba'), (employee) => employee.walletTotal, 'strong'),
      moneyColumn('cashTotal', tt('Hotovosť'), (employee) => employee.cashTotal, 'emerald'),
      moneyColumn(
        'cardTerminalTotal',
        tt('Terminál'),
        (employee) => employee.cardTerminalTotal,
        'blue',
      ),
      moneyColumn(
        'cardCourierTotal',
        tt('Kuriér'),
        (employee) => employee.cardCourierTotal,
        'orange',
      ),
      // §9.767, owner O129 - *CardPay online* (it said *CardPay*), and the *QR Pay* column always beside it, so the
      // method columns add up to *Celková tržba* on every row whatever the day held.
      moneyColumn('cardOnlineTotal', cardPayOnlineLabel(), (employee) => employee.cardOnlineTotal, 'violet'),
      moneyColumn('qrPayTotal', qrPayLabel(), (employee) => employee.qrPayTotal, 'pink'),
      {
        key: 'action',
        header: '',
        render: () => <ChevronRight className="w-4 h-4 text-muted-foreground" />,
      },
    ],
    [],
  )

  return (
    <div className="p-5" data-wallet-section={testId}>
      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-1.5">
        {icon} {title}
        <span className="ml-1 text-[10px] font-normal">
          (<T>klikni pre detail</T>)
        </span>
      </p>
      <DataTable<WalletEmployee>
        columns={columns}
        data={wallets}
        rowKey={(employee) => employee.id ?? 0}
        onRowClick={(employee) => employee.id !== undefined && onOpen(employee.id)}
        pageSize={10}
        compact
        emptyMessage={tt('Žiadni zamestnanci')}
      />
    </div>
  )
}

const MONEY_TONES = {
  strong: 'text-base font-black',
  emerald: 'text-xs font-bold text-emerald-500',
  blue: 'text-xs font-bold text-blue-400',
  orange: 'text-xs font-bold text-orange-400',
  violet: 'text-xs font-bold text-violet-400',
  amber: 'text-xs font-bold text-amber-400',
  pink: 'text-xs font-bold text-pink-400',
} as const

function moneyColumn(
  key: string,
  header: string,
  pick: (employee: WalletEmployee) => WalletEmployee['walletTotal'],
  tone: keyof typeof MONEY_TONES,
): Column<WalletEmployee> {
  return {
    key,
    header,
    sortable: true,
    sortValue: (employee) => parseMoneyToCents(pick(employee)),
    className: 'text-right',
    headerClassName: 'text-right',
    render: (employee) => (
      <span className={cn('tabular-nums', MONEY_TONES[tone])}>
        {formatCents(parseMoneyToCents(pick(employee)))}
      </span>
    ),
  }
}

// ─── the per-employee detail ─────────────────────────────────────────────────

interface WalletOrder {
  id?: number
  orderNumber?: string
  type?: string
  status?: string
  customerName?: string | null
  customerAddress?: string | null
  tableName?: string | null
  total?: number | string
  paymentMethod?: string
  createdAt?: string
  durationMinutes?: number | null
}

interface WalletDetailSummary {
  totalRevenue?: number | string
  orderCount?: number
  cashTotal?: number | string
  cardTotal?: number | string
  cardTerminalTotal?: number | string
  cardCourierTotal?: number | string
  cardOnlineTotal?: number | string
  /** §9.741, BACKEND.md §10A.390 - the fifth part; `cardTotal` does not count it. */
  qrPayTotal?: number | string
}

interface WalletGroup {
  address?: string
  tableName?: string
  count?: number
  total?: number | string
}

function DriverDetail({
  driverId,
  dateFrom,
  dateTo,
  onClose,
}: {
  driverId: number
  /** §9.696, FB-1004 - the page's window, both ends. This sent one day for a row read over five. */
  dateFrom: string
  dateTo: string
  onClose: () => void
}) {
  const { data, isLoading } = useGetEmployeesIdDriverWallet(driverId, { dateFrom, dateTo })
  const detail = data as
    | {
        employee?: WalletEmployee
        summary?: WalletDetailSummary
        orders?: WalletOrder[]
        addresses?: WalletGroup[]
        sales?: unknown
      }
    | undefined

  return (
    <WalletModal
      loading={isLoading}
      name={detail?.employee?.name}
      role={tt('Šofér')}
      dateFrom={dateFrom}
      dateTo={dateTo}
      summary={detail?.summary}
      orders={detail?.orders ?? []}
      groups={detail?.addresses ?? []}
      // §9.666 - the breakdown §10A.336 answers beside the addresses.
      sales={salesBreakdownOf(detail?.sales)}
      groupTab="Adresy"
      groupHeader={tt('Adresa')}
      groupOf={(group) => group.address ?? ''}
      onClose={onClose}
    />
  )
}

function WaiterDetail({
  waiterId,
  dateFrom,
  dateTo,
  onClose,
}: {
  waiterId: number
  /** §9.696, FB-1004 - as the driver's. */
  dateFrom: string
  dateTo: string
  onClose: () => void
}) {
  const { data, isLoading } = useGetEmployeesIdWaiterWallet(waiterId, { dateFrom, dateTo })
  const detail = data as
    | {
        employee?: WalletEmployee
        summary?: WalletDetailSummary
        orders?: WalletOrder[]
        tables?: WalletGroup[]
        sales?: unknown
      }
    | undefined

  return (
    <WalletModal
      loading={isLoading}
      name={detail?.employee?.name}
      role={tt('Čašník')}
      dateFrom={dateFrom}
      dateTo={dateTo}
      summary={detail?.summary}
      orders={detail?.orders ?? []}
      groups={detail?.tables ?? []}
      // §9.666 - the breakdown §10A.336 answers beside the tables.
      sales={salesBreakdownOf(detail?.sales)}
      groupTab={tt('Stoly')}
      groupHeader={tt('Stôl')}
      groupOf={(group) => group.tableName ?? ''}
      onClose={onClose}
    />
  )
}

function WalletModal({
  loading,
  name,
  role,
  dateFrom,
  dateTo,
  summary,
  orders,
  groups,
  sales,
  groupTab,
  groupHeader,
  groupOf,
  onClose,
}: {
  loading: boolean
  name: string | undefined
  role: string
  dateFrom: string
  dateTo: string
  summary: WalletDetailSummary | undefined
  orders: WalletOrder[]
  groups: WalletGroup[]
  /** §9.666, owner O45 and O50 - what they sold, by category and by item. */
  sales: SalesCategoryRow[]
  groupTab: string
  groupHeader: string
  groupOf: (group: WalletGroup) => string
  onClose: () => void
}) {
  // §9.94 — the franchise's own names for its channels, so a rename reaches this table.
  const { data: channelData } = useGetSalesChannels()
  const salesChannels = useMemo(() => filterChannelsOf(channelData), [channelData])

  const [tab, setTab] = useState<'orders' | 'groups' | 'sales'>('orders')
  /* §9.666, owner O50 - *"there will be checkbox and he can select each item (or whole category) and it will
     calculate it at the bottom to him"*. Items only; a category tick is a shorthand for all of them, which is why
     `categoryState` has three answers. */
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set())
  const [openCategories, setOpenCategories] = useState<ReadonlySet<string>>(new Set())
  const picked = useMemo(() => selectedTotals(sales, ticked), [sales, ticked])
  /* §9.695, owner #14 / O67 - *"on si potom nevie vypočítať tržbu ... keď mu nesedia peniaze"*. Every row together,
     which §10A.352 makes equal to *Celkovo* above, and how far apart the two are when a server older than it answers
     the gross figures instead. */
  const salesTotal = useMemo(() => salesTotals(sales), [sales])
  const salesGap = salesGapCents(sales, parseMoneyToCents(summary?.totalRevenue))

  const orderColumns: Column<WalletOrder>[] = useMemo(
    () => [
      {
        key: 'orderNumber',
        header: '#',
        sortable: true,
        sortValue: (order) => order.orderNumber,
        // §9.143 — the takings table is a record; each row's order opens in the archive.
        render: (order) => (
          <OrderNumberLink
            orderId={order.id}
            className="font-mono text-xs font-bold text-primary hover:underline"
            title={`${tt('Otvoriť v archíve')}: #${order.orderNumber}`}
          >
            #{order.orderNumber}
          </OrderNumberLink>
        ),
      },
      {
        key: 'type',
        header: 'Typ',
        sortable: true,
        sortValue: (order) => typeLabel(order.type, salesChannels),
        render: (order) => (
          <StatusBadge
            label={typeLabel(order.type, salesChannels)}
            color={TYPE_COLORS[order.type ?? ''] ?? 'zinc'}
          />
        ),
      },
      {
        key: 'customer',
        header: tt('Zákazník'),
        sortable: true,
        sortValue: (order) => order.customerName ?? '',
        render: (order) => (
          <div className="min-w-0">
            {order.customerName && (
              <p className="text-sm font-semibold truncate">{order.customerName}</p>
            )}
            {order.customerAddress && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                <MapPin className="w-3 h-3 shrink-0" />
                {order.customerAddress}
              </p>
            )}
            {order.tableName && (
              <p className="text-xs text-muted-foreground truncate">{order.tableName}</p>
            )}
          </div>
        ),
      },
      {
        key: 'payment',
        header: tt('Platba'),
        sortable: true,
        sortValue: (order) => paymentLabel(order.paymentMethod),
        render: (order) => (
          <span className="text-xs text-muted-foreground">{paymentLabel(order.paymentMethod)}</span>
        ),
      },
      {
        key: 'createdAt',
        header: tt('Dátum'),
        sortable: true,
        sortValue: (order) => new Date(order.createdAt ?? 0).getTime(),
        render: (order) => (
          <p className="text-xs text-muted-foreground whitespace-nowrap">
            {order.createdAt ? formatDateTime(order.createdAt) : ''}
            {order.durationMinutes != null && ` · ${Math.round(order.durationMinutes)} min`}
          </p>
        ),
      },
      {
        key: 'total',
        header: 'Suma',
        sortable: true,
        sortValue: (order) => parseMoneyToCents(order.total),
        className: 'text-right',
        headerClassName: 'text-right',
        render: (order) => (
          <span className="font-bold text-sm whitespace-nowrap tabular-nums">
            {formatCents(parseMoneyToCents(order.total))}
          </span>
        ),
      },
    ],
    // §9.94 — the columns read the franchise's channel names, so they rebuild when those arrive.
    [salesChannels],
  )

  const groupColumns: Column<WalletGroup>[] = useMemo(
    () => [
      {
        key: 'label',
        header: groupHeader,
        sortable: true,
        sortValue: (group) => groupOf(group),
        render: (group) => (
          <p className="text-sm font-semibold truncate flex items-center gap-1">
            <MapPin className="w-3 h-3 text-muted-foreground shrink-0" />
            {groupOf(group)}
          </p>
        ),
      },
      {
        key: 'count',
        header: tt('Návštevy'),
        sortable: true,
        sortValue: (group) => group.count ?? 0,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (group) => (
          <span className="text-xs font-bold text-primary whitespace-nowrap">
            {group.count ?? 0}×
          </span>
        ),
      },
      {
        key: 'total',
        header: 'Suma',
        sortable: true,
        sortValue: (group) => parseMoneyToCents(group.total),
        className: 'text-right',
        headerClassName: 'text-right',
        render: (group) => (
          <span className="text-sm font-semibold tabular-nums">
            {formatCents(parseMoneyToCents(group.total))}
          </span>
        ),
      },
    ],
    [groupHeader, groupOf],
  )

  /* §9.495, `TODO.md` 3f - it said `aria-modal="true"` and confined nothing. Wrapper is the dismiss scrim, so
     the role stays on it and the trap is bounded by the card (§9.469); the close matches the scrim's own tap,
     which is unconditional here because this overlay holds no write of its own (§9.491). */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, onClose)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${name ?? role} - ${tt('detail peňaženky')}`}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        ref={panelRef}
        onKeyDown={onKeyDown}
        data-wallet-detail
      >
        <div className="flex items-center gap-3 px-6 py-4 border-b border-border shrink-0">
          <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-primary text-sm font-bold">
            {initialsOf(name) || '?'}
          </div>
          <div>
            <p className="font-bold">{loading ? tt('Načítavam...') : name}</p>
            <p className="text-xs text-muted-foreground">
              {role} · {windowLabel(dateFrom, dateTo)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            data-dialog-cancel
            className="ml-auto p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <Spinner />
          </div>
        ) : !summary ? (
          <div className="flex-1 flex items-center justify-center py-12 text-muted-foreground text-sm">
            <T>Dáta nie sú dostupné</T>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-5 border-b border-border">
              <StatCard
                label="Celkovo"
                value={formatCents(parseMoneyToCents(summary.totalRevenue))}
                color="primary"
              />
              <StatCard
                label={tt('Objednávok')}
                value={String(summary.orderCount ?? 0)}
                color="primary"
              />
              <StatCard
                label={tt('Hotovosť')}
                value={formatCents(parseMoneyToCents(summary.cashTotal))}
                icon={Banknote}
                color="emerald"
              />
              <StatCard
                label={tt('Terminál')}
                value={formatCents(parseMoneyToCents(summary.cardTerminalTotal))}
                icon={CreditCard}
                color="blue"
              />
              <StatCard
                label={tt('Kuriér karta')}
                value={formatCents(parseMoneyToCents(summary.cardCourierTotal))}
                icon={CreditCard}
                color="orange"
              />
              {/* §9.767 - *CardPay online* (it said *CardPay*) and QR Pay always beside it. */}
              <StatCard
                label={cardPayOnlineLabel()}
                value={formatCents(parseMoneyToCents(summary.cardOnlineTotal))}
                icon={Globe}
                color="violet"
                testId="person-cardpay-online"
              />
              <StatCard
                label={qrPayLabel()}
                value={qrPayFigure(parseMoneyToCents(summary.qrPayTotal))}
                icon={QrCode}
                color="pink"
                testId="person-qr-pay"
              />
              {/* §9.767 - says what it holds: the server's `cardTotal` is the terminal, the courier and CardPay online,
                * and not QR Pay, so a reader adding the tiles beside it can tell why it is not their sum. */}
              <StatCard
                label={tt('Karta spolu')}
                value={formatCents(parseMoneyToCents(summary.cardTotal))}
                sub={tt('terminál, kuriér a CardPay online - bez QR Pay')}
                icon={CreditCard}
                color="primary"
                testId="person-card-total"
              />
            </div>

            <div className="px-5 pt-4">
              <TabGroup
                tabs={[
                  { id: 'orders', label: tt('Objednávky'), count: orders.length },
                  { id: 'groups', label: groupTab, count: groups.length },
                  // §9.666, owner O45 - the owner's own name for it.
                  // §9.695 - the categories sold, not the four rows that close the sum.
                  { id: 'sales', label: tt('Prehľad predaja'), count: sales.filter((row) => row.kind === 'goods').length },
                ]}
                active={tab}
                onChange={(id) => setTab(id as 'orders' | 'groups' | 'sales')}
                className="mb-0"
              />
            </div>

            {tab === 'sales' ? (
              /* §9.666, owner O45 and O50. A hand-built table rather than `DataTable`, because a row expands into
                 its own rows and carries a checkbox - two things that component has no shape for, and bending it
                 would change it for the forty screens that use it. */
              <div className="p-5 space-y-3">
                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-xs" data-sales-breakdown>
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="w-8 px-2 py-2" />
                        <th className="px-2 py-2 text-left font-bold"><T>Kategória</T></th>
                        <th className="px-2 py-2 text-right font-bold"><T>Počet</T></th>
                        <th className="px-2 py-2 text-right font-bold"><T>Obj.</T></th>
                        <th className="px-2 py-2 text-right font-bold"><T>Tržba</T></th>
                        {SALES_MONEY_COLUMNS.map((column) => (
                          <th key={column} className="px-2 py-2 text-right font-bold whitespace-nowrap">
                            {salesColumnLabel(column)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sales.length === 0 && (
                        <tr>
                          <td colSpan={5 + SALES_MONEY_COLUMNS.length} className="px-3 py-6 text-center text-muted-foreground">
                            <T>Za toto obdobie nič nepredal.</T>
                          </td>
                        </tr>
                      )}
                      {sales.map((category) => {
                        const state = categoryState(category, ticked)
                        const open = openCategories.has(category.category)
                        return (
                          <Fragment key={category.category}>
                            <tr
                              className={cn(
                                'border-t border-border',
                                category.kind === 'goods' ? 'bg-muted/20' : 'bg-primary/5 italic',
                              )}
                              data-sales-category={category.category}
                              data-sales-kind={category.kind}
                            >
                              <td className="px-2 py-2">
                                <input
                                  type="checkbox"
                                  checked={state === 'all'}
                                  // §9.666 - *some* is neither on nor off, and a box showing *off* there would
                                  // un-tick the rest the moment somebody pressed it.
                                  ref={(box) => {
                                    if (box) box.indeterminate = state === 'some'
                                  }}
                                  onChange={() => setTicked(toggleCategory(category, ticked))}
                                  aria-label={`${tt('Vybrať kategóriu')} ${category.category}`}
                                  data-sales-category-tick={category.category}
                                  className="w-4 h-4 rounded accent-primary"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setOpenCategories((current) => {
                                      const next = new Set(current)
                                      if (next.has(category.category)) next.delete(category.category)
                                      else next.add(category.category)
                                      return next
                                    })
                                  }
                                  data-sales-expand={category.category}
                                  className="font-bold hover:underline flex items-center gap-1"
                                >
                                  <span className="text-muted-foreground">{open ? '-' : '+'}</span>
                                  {/* §9.695 - a closing row is named through the translator, never as the server's key. */}
                                  {salesRowLabel(category)}
                                </button>
                              </td>
                              <td className="px-2 py-2 text-right font-bold">{category.quantity}</td>
                              <td className="px-2 py-2 text-right">{category.orders}</td>
                              <td className="px-2 py-2 text-right font-bold">{formatCents(category.revenue)}</td>
                              {SALES_MONEY_COLUMNS.map((column) => (
                                <td key={column} className="px-2 py-2 text-right">
                                  {formatCents(category[column])}
                                </td>
                              ))}
                            </tr>
                            {open &&
                              category.items.map((item) => (
                                <tr
                                  key={item.item}
                                  className="border-t border-border/50"
                                  data-sales-item={item.item}
                                >
                                  <td className="px-2 py-1.5 pl-6">
                                    <input
                                      type="checkbox"
                                      checked={ticked.has(salesItemKey(category.category, item.item))}
                                      onChange={() =>
                                        setTicked(toggleItem(category.category, item.item, ticked))
                                      }
                                      aria-label={`${tt('Vybrať')} ${item.item}`}
                                      data-sales-item-tick={item.item}
                                      className="w-4 h-4 rounded accent-primary"
                                    />
                                  </td>
                                  {/* §9.695 - the delivery and the rounding are one line each, named as their row is. */}
                                  <td className="px-2 py-1.5 pl-6">
                                    {category.kind === 'delivery' || category.kind === 'rounding'
                                      ? salesRowLabel(category)
                                      : item.item}
                                  </td>
                                  <td className="px-2 py-1.5 text-right">{item.quantity}</td>
                                  <td className="px-2 py-1.5 text-right">{item.orders}</td>
                                  <td className="px-2 py-1.5 text-right">{formatCents(item.revenue)}</td>
                                  {SALES_MONEY_COLUMNS.map((column) => (
                                    <td key={column} className="px-2 py-1.5 text-right">
                                      {formatCents(item[column])}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                          </Fragment>
                        )
                      })}
                    </tbody>
                    {/* §9.695, owner #14 / O67 - every row together. §10A.352 makes it equal *Celkovo* above, and each
                      * column the card of its own method, which is the reconciliation the owner could not make. */}
                    {sales.length > 0 && (
                      <tfoot>
                        <tr className="border-t-2 border-border font-black" data-sales-total>
                          <td className="px-2 py-2" />
                          <td className="px-2 py-2">
                            <T>Spolu</T>
                          </td>
                          <td className="px-2 py-2" />
                          <td className="px-2 py-2" />
                          <td className="px-2 py-2 text-right" data-sales-total-revenue>
                            {formatCents(salesTotal.revenue)}
                          </td>
                          {SALES_MONEY_COLUMNS.map((column) => (
                            <td key={column} className="px-2 py-2 text-right">
                              {formatCents(salesTotal[column])}
                            </td>
                          ))}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>

                {/* §9.695 - a server older than §10A.352 answers the figures before the discount and without the
                  * delivery, so its rows fall short of *Celkovo*. Said, because two figures on one screen disagreeing
                  * in silence is the owner's complaint itself. */}
                {salesGap !== 0 && (
                  <p className="text-xs text-muted-foreground" data-sales-gap>
                    <T>Riadky sa nezhodujú s Celkovo o</T> <strong>{formatCents(salesGap)}</strong>.
                  </p>
                )}

                {/* §9.666, owner O50 - *"it will calculate it at the bottom to him"*. Nought until something is
                  * ticked, because a total that starts as the whole table reads as a figure somebody chose. */}
                <div
                  className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs"
                  data-sales-selected
                >
                  <span className="font-bold">
                    <T>Vybrané</T>: <span data-sales-selected-items>{picked.items}</span>
                  </span>
                  <span>
                    <T>Počet</T>: <strong data-sales-selected-quantity>{picked.quantity}</strong>
                  </span>
                  <span>
                    <T>Tržba</T>:{' '}
                    <strong data-sales-selected-revenue>{formatCents(picked.revenue)}</strong>
                  </span>
                  {SALES_MONEY_COLUMNS.map((column) => (
                    <span key={column}>
                      {salesColumnLabel(column)}: <strong>{formatCents(picked[column])}</strong>
                    </span>
                  ))}
                  {picked.items > 0 && (
                    <button
                      type="button"
                      onClick={() => setTicked(new Set())}
                      data-sales-clear
                      className="underline font-semibold hover:no-underline ml-auto"
                    >
                      <T>Zrušiť výber</T>
                    </button>
                  )}
                </div>
              </div>
            ) : tab === 'orders' ? (
              <div className="p-5">
                <DataTable<WalletOrder>
                  columns={orderColumns}
                  data={orders}
                  rowKey={(order) => order.id ?? 0}
                  pageSize={10}
                  compact
                  emptyMessage={tt('Žiadne objednávky')}
                />
              </div>
            ) : (
              <div className="p-5">
                <DataTable<WalletGroup>
                  columns={groupColumns}
                  data={groups}
                  rowKey={(_, index) => index}
                  pageSize={10}
                  compact
                  emptyMessage={tt('Žiadne záznamy')}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * The window a figure covers - one day as legacy labels it, or both ends - §9.665, §9.696.
 *
 * One function for the page's label and the popup's header, so the two cannot name different windows: FB-1004 was
 * the popup reading one day under a page that showed five.
 */
function windowLabel(from: string, to: string): string {
  return from === to ? dayLabel(from) : `${dayLabel(from)} - ${dayLabel(to)}`
}

/** `Dnes`, `Včera`, or the date — legacy's own labelling. */
function dayLabel(date: string): string {
  if (date === today()) return tt('Dnes')
  if (date === yesterday()) return tt('Včera')
  return formatDay(`${date}T12:00:00`)
}
